import { DocumentDuplicateIcon,ExclamationTriangleIcon  } from '@heroicons/react/20/solid';
import { format, isValid, parse } from 'date-fns';
import React, { useEffect, useState } from 'react';

import { FavoriteButton } from '@/components/common/FavoriteButton';
import { AttorneyLink } from '@/components/entity-links/entities/AttorneyLink';
import { Tooltip } from '@/components/ui/v3/Tooltip';
import WorkflowIndicator from '@/components/WorkflowIndicator';
import { useGetAttorneyQuery } from '@/services/api/attorneysService';
import { extractAttorneyId } from '@/utils/attorney-helpers';
import {
  getDaysUntilSol,
  getSolSeverity,
  SOL_SEVERITY_STYLES,
  type SolSeverity,
} from '@/utils/solUtils';

// All badges from local barrel export
import {
  CaseTypeEditBadge,
  ClientContactWarning,
  CMSEmailBadge,
  CMSLinkBadge,
  DateOfLossEditBadge,
  ExternalLinkBadge,
  FiledDateEditBadge,
  InfoBadge,
  LastContactEditBadge,
  LocationEditBadge,
  MatterStatusBadge,
  SolEditBadge,
} from './badges';
import { MatterFirmLink } from './MatterFirmLink';
import { MatterTitleDisplayProps } from './types';

function formatSolBannerText(days: number): string {
  if (days < 0) return `expired ${Math.abs(days)} days ago`;
  if (days === 0) return 'expires today';
  return `in ${days} days`;
}

export const MatterTitleDisplay: React.FC<MatterTitleDisplayProps> = ({
  matterData,
  name,
  status,
  type,
  dateOfLoss,
  sol,
  city,
  state,
  contactClientDirectly,
  onEditClick,
}) => {
  const showLocation = city && state;

  const firmId = matterData?.firm?.id as string | undefined;
  const attyId = extractAttorneyId(matterData?.representing_attorney);
  const { data: _atty } = useGetAttorneyQuery(
    { firm_id: firmId || '', id: attyId || '' },
    { skip: !firmId || !attyId },
  );

  const atty = attyId ? _atty : null;

  const handleCopyMatterId = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!matterData?.id) return;

    navigator.clipboard.writeText(matterData.id).catch(() => {
      // Silent catch - clipboard API may fail
    });
  };

  const formatDateForDisplay = (
    dateString: string | undefined | null,
  ): string => {
    if (!dateString) return '';

    try {
      // Try parsing as ISO date first (YYYY-MM-DD)
      const parsed = parse(dateString, 'yyyy-MM-dd', new Date());
      if (isValid(parsed)) {
        return format(parsed, 'MM/dd/yyyy');
      }

      // If that fails, try direct Date parsing
      const date = new Date(dateString);
      if (isValid(date)) {
        return format(date, 'MM/dd/yyyy');
      }
    } catch {
      // Silent catch - return original string on error
    }

    return dateString;
  };

  const formattedSol = formatDateForDisplay(sol);
  const formattedCreatedAt = formatDateForDisplay(matterData?.created_at);

  // SOL severity calculations
  // Hide all SOL indicators when filed_date is set (filing suit tolls the SOL)
  const isFiledDateSet = !!matterData?.filed_date;
  const daysUntilSol = getDaysUntilSol(sol);
  const solSeverity: SolSeverity = getSolSeverity(sol);
  const showSolBanner =
    !isFiledDateSet && (solSeverity === 'critical' || solSeverity === 'urgent');
  const showSolModal = !isFiledDateSet && solSeverity === 'critical' && daysUntilSol !== null;

  // Banner dismiss state (per session)
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Acknowledgment modal state
  const [showAckModal, setShowAckModal] = useState(false);
  const [ackReason, setAckReason] = useState('');

  // Show modal on mount if < 7 days; dismiss if status transitions to Litigation
  useEffect(() => {
    if (showSolModal && matterData?.id) {
      const ackKey = `sol_ack_${matterData.id}_${new Date().toISOString().split('T')[0]}`;
      const alreadyAcked = sessionStorage.getItem(ackKey);
      if (!alreadyAcked) {
        setShowAckModal(true);
      }
    } else {
      setShowAckModal(false);
    }
  }, [showSolModal, matterData?.id]);

  const handleAcknowledge = () => {
    if (!ackReason) return;
    if (matterData?.id) {
      const ackKey = `sol_ack_${matterData.id}_${new Date().toISOString().split('T')[0]}`;
      sessionStorage.setItem(ackKey, JSON.stringify({
        reason: ackReason,
        timestamp: new Date().toISOString(),
      }));
    }
    setShowAckModal(false);
    setAckReason('');
  };

  return (
    <div className="w-full bg-white border-zinc-100">
      {/* SOL Warning Banner (< 30 days) — negative margin to break out of parent px-4 */}
      {showSolBanner && !bannerDismissed && daysUntilSol !== null && (
        <div
          className={`-mx-4 flex items-center justify-between px-4 py-1.5 border-b text-xs font-medium ${SOL_SEVERITY_STYLES.banner[solSeverity]}`}
        >
          <div className="flex items-center gap-1.5">
            <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" />
            <span>
              Statute of limitations{' '}
              {formatSolBannerText(daysUntilSol)}{' '}
              — {formattedSol}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setBannerDismissed(true)}
            className="text-current opacity-60 hover:opacity-100 transition-opacity p-0.5"
            aria-label="Dismiss SOL warning"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* SOL Acknowledgment Modal (< 7 days) */}
      {showAckModal && daysUntilSol !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-red-100">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-base font-semibold text-zinc-900">
                SOL Deadline Alert
              </h3>
            </div>
            <p className="text-sm text-zinc-600 mb-4">
              This matter&apos;s statute of limitations{' '}
              {formatSolBannerText(daysUntilSol)}{' '}
              ({formattedSol}). Please confirm your plan.
            </p>
            <div className="space-y-2 mb-4">
              {[
                { value: 'filing_soon', label: 'Filing soon' },
                { value: 'settlement', label: 'Settlement in progress' },
                { value: 'referring_out', label: 'Referring out' },
                { value: 'need_help', label: 'Need help' },
                { value: 'other', label: 'Other' },
              ].map((option) => (
                <label
                  key={option.value}
                  htmlFor={`sol_ack_${option.value}`}
                  className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                    ackReason === option.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-zinc-200 hover:bg-zinc-50'
                  }`}
                >
                  <input
                    id={`sol_ack_${option.value}`}
                    type="radio"
                    name="sol_ack_reason"
                    value={option.value}
                    checked={ackReason === option.value}
                    onChange={(e) => setAckReason(e.target.value)}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm font-medium text-zinc-700">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
            <button
              type="button"
              onClick={handleAcknowledge}
              disabled={!ackReason}
              className="w-full py-2 px-4 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Acknowledge SOL Deadline
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between px-4 py-2 gap-4 min-h-[44px] overflow-hidden">
        {/* Left Group: Identity + MP/CM subtitle */}
        <div className="flex flex-col shrink-0">
        <div className="flex items-center gap-2">
          {/* Favorite Star */}
          {matterData?.id && name && (
            <FavoriteButton
              matterId={matterData.id}
              matterName={name}
              size="xs"
              showToast
              className="shrink-0"
            />
          )}

          {/* ID Copy (Subtle) */}
          {matterData?.id && (
            <button
              type="button"
              onClick={handleCopyMatterId}
              className="text-zinc-300 hover:text-zinc-500 transition-colors p-0.5 shrink-0"
              title="Copy Matter ID"
            >
              <DocumentDuplicateIcon className="h-3 w-3" />
            </button>
          )}

          {/* Matter Name */}
          <button
            type="button"
            onClick={onEditClick}
            onKeyDown={(e) => e.key === 'Enter' && onEditClick()}
            className="font-semibold text-sm text-zinc-900 hover:text-blue-600 transition-colors whitespace-nowrap shrink-0"
          >
            {name}
          </button>

          {/* Separator + Firm */}
          {matterData?.firm && (
            <>
              <span className="text-zinc-300 shrink-0">/</span>
              <MatterFirmLink
                firmId={matterData.firm.id}
                caseManager={matterData.case_manager}
                className="text-zinc-500 hover:text-zinc-800 font-medium text-sm transition-colors whitespace-nowrap shrink-0"
              >
                {matterData.firm.name}
              </MatterFirmLink>
            </>
          )}

          {/* Attorney - hide on smaller screens */}
          {atty && firmId && (
            <span className="text-zinc-400 text-xs font-medium hidden lg:inline truncate max-w-[200px]">
              •{' '}
              <AttorneyLink
                entityId={atty.id}
                firmId={firmId}
                className="text-zinc-400 hover:text-zinc-600"
              >
                {atty.first_name} {atty.last_name}
              </AttorneyLink>
            </span>
          )}

          {/* Workflow & Warnings */}
          {matterData?.id && (
            <div className="flex items-center gap-1.5 shrink-0">
              <WorkflowIndicator matterId={matterData.id} />
              {contactClientDirectly === false && (
                <ClientContactWarning onClick={onEditClick} />
              )}
            </div>
          )}
        </div>

          {/* MP / CM subtitle row */}
          {(() => {
            const mp = matterData?.firm?.managing_paralegal;
            const mpName = mp?.first_name || mp?.last_name
              ? `${mp.first_name || ''} ${mp.last_name || ''}`.trim()
              : null;
            const cmName = matterData?.case_manager?.display_name || null;
            if (!mpName && !cmName) return null;
            return (
              <div className="flex items-center gap-1.5 pl-9 -mt-0.5 text-[11px] font-medium text-zinc-500 leading-tight">
                {mpName && <span>MP: {mpName}</span>}
                {mpName && cmName && <span className="text-zinc-400">·</span>}
                {cmName && <span>CM: {cmName}</span>}
              </div>
            );
          })()}
        </div>

        {/* Right Group: Compact Metadata - single line, can shrink to accommodate names */}
        <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
          {/* Status - always visible */}
          {status && matterData?.id && (
            <MatterStatusBadge
              matterId={matterData.id}
              matterName={name || ''}
              currentStatus={status}
            />
          )}

          {/* Type - hide on small */}
          {matterData?.id && (
            <div className="hidden md:block">
              <CaseTypeEditBadge
                matterId={matterData.id}
                value={type}
                className="bg-transparent hover:bg-zinc-50 border-transparent hover:border-zinc-100"
              />
            </div>
          )}

          {/* DOL - inline editable, shows "Not set" when missing */}
          {matterData?.id && (
            <div className="hidden lg:block">
              <DateOfLossEditBadge
                matterId={matterData.id}
                value={dateOfLoss}
              />
            </div>
          )}

          {/* Filed Date badge */}
          {matterData?.id && (
            <div className="hidden lg:block">
              <FiledDateEditBadge
                matterId={matterData.id}
                value={matterData.filed_date}
                className="bg-transparent hover:bg-zinc-50 border-transparent hover:border-zinc-100"
              />
            </div>
          )}

          {/* SOL - inline editable, color-coded by proximity; hidden when filed_date is set */}
          {matterData?.id && !isFiledDateSet && (
            <div className="hidden lg:block">
              <SolEditBadge
                matterId={matterData.id}
                value={sol}
              />
            </div>
          )}

          {/* Created Date - hide on smaller */}
          {formattedCreatedAt && (
            <div className="hidden xl:block">
              <InfoBadge
                label="Opened"
                value={formattedCreatedAt}
                showLabel
                className="bg-transparent hover:bg-zinc-50 border-transparent hover:border-zinc-100"
              />
            </div>
          )}

          {/* Last Contacted */}
          {matterData?.last_contacted_at && matterData?.id && (
            <>
              <div className="h-4 w-px bg-zinc-200 mx-1 shrink-0 hidden xl:block" />
              <LastContactEditBadge
                matterId={matterData.id}
                value={matterData.last_contacted_at}
              />
            </>
          )}

          {/* Location */}
          {showLocation && matterData?.id && (
            <>
              <div className="h-4 w-px bg-zinc-200 mx-1 shrink-0 hidden xl:block" />
              <LocationEditBadge
                matterId={matterData.id}
                city={city}
                state={state}
                className="bg-transparent hover:bg-zinc-50 border-transparent hover:border-zinc-100"
              />
            </>
          )}

          {/* Retainer - only show badge when retainer is NOT signed */}
          {!matterData?.retainer_signed && (
            <>
              <div className="h-4 w-px bg-zinc-200 mx-1 shrink-0 hidden xl:block" />
              <Tooltip content="Click to edit matter details">
                <InfoBadge
                  label="Retainer"
                  value="Unsigned"
                  onClick={onEditClick}
                  showLabel={false}
                  className="bg-red-50 border border-red-100"
                  valueClassName="text-red-700"
                />
              </Tooltip>
            </>
          )}

          {/* Links - always visible */}
          {matterData && (
            <div className="flex items-center gap-1">
              <CMSLinkBadge
                matterId={matterData.id}
                cmsUrl={matterData.external_cms_url}
              />
              <CMSEmailBadge
                matterId={matterData.id}
                cmsEmail={matterData.cms_email}
              />
              {matterData.intake_id && (
                <ExternalLinkBadge
                  href={`/intakes/${matterData.intake_id}`}
                  label="Intake"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
